import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Music } from 'lucide-react';
import trackingService from '../services/tracking';

const BAR_COUNT = 48;
const GAP = 2;

const optimizedCover = (url) => {
    if (!url || url.endsWith('-opt.webp')) return url;
    const dot = url.lastIndexOf('.');
    return dot !== -1 ? url.slice(0, dot) + '-opt.webp' : url;
};

const AudioPlayer = ({ src, coverImageUrl, title, hideCoverArt = false, uploadId = null, locked = false, onLockedClick = null }) => {
    const rowHeight = 100;
    const coverPx = 96;
    const audioRef = useRef(null);
    const canvasRef = useRef(null);
    const animFrameRef = useRef(null);
    const idleTimerRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const gradCacheRef = useRef({ grad: null, h: -1 });
    const isWiredRef = useRef(false);
    const isPlayingRef = useRef(false);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    const fmt = (s) => {
        if (!s || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    const drawIdle = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || canvas.width === 0) return;
        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;
        ctx.clearRect(0, 0, W, H);
        const barW = Math.max(2, (W - GAP * (BAR_COUNT - 1)) / BAR_COUNT);
        const t = Date.now() * 0.0015;
        ctx.fillStyle = 'rgba(124, 58, 237, 0.28)';
        for (let i = 0; i < BAR_COUNT; i++) {
            const v = (Math.sin(i * 0.38 + t) * 0.5 + 0.5) * 0.18 + 0.04;
            const barH = Math.max(2, v * H);
            ctx.fillRect(i * (barW + GAP), H - barH, barW, barH);
        }
    }, []);

    const stopIdleAnimation = useCallback(() => {
        if (idleTimerRef.current) {
            clearInterval(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    }, []);

    const startIdleAnimation = useCallback(() => {
        stopIdleAnimation();
        drawIdle();
        idleTimerRef.current = setInterval(drawIdle, 250); // ~4fps — negligible CPU
    }, [drawIdle, stopIdleAnimation]);

    const stopLiveAnimation = useCallback(() => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }
    }, []);

    const startLiveAnimation = useCallback(() => {
        stopLiveAnimation();
        const render = () => {
            if (!isPlayingRef.current) return; // stop loop if paused mid-frame
            const canvas = canvasRef.current;
            if (!canvas || canvas.width === 0) {
                animFrameRef.current = requestAnimationFrame(render);
                return;
            }
            const ctx = canvas.getContext('2d');
            const W = canvas.width;
            const H = canvas.height;
            ctx.clearRect(0, 0, W, H);
            const barW = Math.max(2, (W - GAP * (BAR_COUNT - 1)) / BAR_COUNT);

            const analyser = analyserRef.current;
            if (analyser) {
                if (gradCacheRef.current.h !== H) {
                    const g = ctx.createLinearGradient(0, H, 0, 0);
                    g.addColorStop(0, 'rgba(109, 40, 217, 0.95)');
                    g.addColorStop(0.55, 'rgba(168, 85, 247, 0.9)');
                    g.addColorStop(1, 'rgba(236, 72, 153, 0.85)');
                    gradCacheRef.current = { grad: g, h: H };
                }
                const data = new Uint8Array(analyser.frequencyBinCount);
                analyser.getByteFrequencyData(data);
                ctx.fillStyle = gradCacheRef.current.grad;
                for (let i = 0; i < BAR_COUNT; i++) {
                    const bin = Math.floor((i / BAR_COUNT) * analyser.frequencyBinCount);
                    const barH = Math.max(3, (data[bin] / 255) * H * 0.92);
                    ctx.fillRect(i * (barW + GAP), H - barH, barW, barH);
                }
            }
            animFrameRef.current = requestAnimationFrame(render);
        };
        animFrameRef.current = requestAnimationFrame(render);
    }, [stopLiveAnimation]);

    // Size canvas to rendered dimensions
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);
        return () => ro.disconnect();
    }, []);

    // Start idle animation on mount
    useEffect(() => {
        startIdleAnimation();
        return () => {
            stopIdleAnimation();
            stopLiveAnimation();
        };
    }, [startIdleAnimation, stopIdleAnimation, stopLiveAnimation]);

    // Cleanup AudioContext on unmount
    useEffect(() => {
        return () => {
            audioCtxRef.current?.close();
            audioCtxRef.current = null;
            analyserRef.current = null;
            isWiredRef.current = false;
        };
    }, []);

    const wireAudio = () => {
        if (isWiredRef.current || !audioRef.current) return;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        analyser.smoothingTimeConstant = 0.8;
        const source = ctx.createMediaElementSource(audioRef.current);
        source.connect(analyser);
        analyser.connect(ctx.destination);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        isWiredRef.current = true;
    };

    const handlePlay = () => {
        setIsPlaying(true);
        isPlayingRef.current = true;
        stopIdleAnimation();
        startLiveAnimation();
        if (uploadId && audioRef.current) {
            trackingService.startPlayTracking(uploadId, audioRef.current);
        }
    };

    const handlePause = () => {
        setIsPlaying(false);
        isPlayingRef.current = false;
        stopLiveAnimation();
        startIdleAnimation();
        if (uploadId) {
            trackingService.stopPlayTracking(uploadId);
        }
    };

    // Stop tracking if this player unmounts while still playing (e.g. list re-render)
    useEffect(() => {
        return () => {
            if (uploadId) {
                trackingService.stopPlayTracking(uploadId);
            }
        };
    }, [uploadId]);

    const togglePlay = async (e) => {
        e.stopPropagation();
        wireAudio();
        const audio = audioRef.current;
        if (!audio) return;
        if (audioCtxRef.current?.state === 'suspended') {
            await audioCtxRef.current.resume();
        }
        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(() => {});
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleSeek = (e) => {
        e.stopPropagation();
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audio.currentTime = pct * duration;
        setCurrentTime(pct * duration);
    };

    const progress = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div
            className="relative rounded-xl overflow-hidden border border-purple-500/20 bg-gray-900/80 mb-3"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Visualizer + Cover art row */}
            <div className="flex" style={{ height: `${rowHeight}px` }}>
                {/* Cover art */}
                {!hideCoverArt && (
                    <div className="flex-shrink-0 h-full relative overflow-hidden" style={{ width: `${coverPx}px` }}>
                        {coverImageUrl ? (
                            <img
                                src={optimizedCover(coverImageUrl)}
                                alt={title || 'Cover art'}
                                width={coverPx}
                                height={rowHeight}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.onerror = null; e.target.src = coverImageUrl; }}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                                <Music className="w-8 h-8 text-purple-300/60" />
                            </div>
                        )}
                        <div className="absolute inset-y-0 right-0 w-4 bg-gradient-to-r from-transparent to-gray-900/80" />
                    </div>
                )}

                {/* Canvas visualizer */}
                <canvas
                    ref={canvasRef}
                    className="flex-1 block bg-gray-950/60"
                    style={{ height: `${rowHeight}px` }}
                />
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-900/60">
                <button
                    onClick={togglePlay}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 hover:bg-purple-500 flex items-center justify-center transition-colors"
                >
                    {isPlaying
                        ? <Pause className="w-3.5 h-3.5 text-white" />
                        : <Play className="w-3.5 h-3.5 text-white ml-0.5" />}
                </button>

                <span className="text-xs text-gray-400 tabular-nums w-8 text-right flex-shrink-0">
                    {fmt(currentTime)}
                </span>

                <div
                    className="flex-1 h-1.5 bg-gray-700 rounded-full cursor-pointer"
                    onClick={handleSeek}
                >
                    <div
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <span className="text-xs text-gray-400 tabular-nums w-8 flex-shrink-0">
                    {fmt(duration)}
                </span>

                <button
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-white transition-colors"
                >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
            </div>

            {/* Hidden audio element */}
            <audio
                ref={audioRef}
                src={src}
                title={title}
                preload="metadata"
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={() => { handlePause(); setCurrentTime(0); }}
                onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.target.duration)}
            />

            {/* Locked overlay — shows the normal player, but intercepts all interaction */}
            {locked && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onLockedClick?.();
                    }}
                    aria-label="Protected content"
                    className="absolute inset-0 z-20 cursor-pointer"
                />
            )}
        </div>
    );
};

export default AudioPlayer;
